import React from "react";
import { Box, Card, Chip, Divider, Link as MuiLink, Stack, Typography, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import type { StudentBlock } from "../types";

// ── Step Up palette shorthand (matches PremiumHeader + index.tsx tokens) ──
const NAVY = "#1b3a6b";
const INK = "#0f172a";
const MUTED = "#64748b";
const HAIRLINE = "#e2e8f0";
const REVIEW_BG = "#fefaf3";
const REVIEW_BORDER = "#f5e5c8";

const to12h = (t: string | null) => {
  if (!t) return "—";
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return t;
  let h = Number(m[1]); const mm = m[2];
  const s = h >= 12 ? "PM" : "AM"; h = h % 12; if (h === 0) h = 12;
  return `${h}:${mm} ${s}`;
};

const formatWeekRange = (from: string, to: string) => {
  const f = new Date(from + "T00:00:00");
  const t = new Date(to + "T00:00:00");
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return `${from} – ${to}`;
  const sameMonth = f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear();
  const fMonth = f.toLocaleString("en-US", { month: "short" });
  const tMonth = t.toLocaleString("en-US", { month: "short" });
  const year = t.getFullYear();
  return sameMonth
    ? `${fMonth} ${f.getDate()} – ${t.getDate()}, ${year}`
    : `${fMonth} ${f.getDate()} – ${tMonth} ${t.getDate()}, ${year}`;
};

const formatDayHeader = (dateIso: string) => {
  const d = new Date(dateIso + "T00:00:00");
  if (isNaN(d.getTime())) return dateIso;
  const month = d.toLocaleString("en-US", { month: "short" });
  return `${month} ${d.getDate()}, ${d.getFullYear()}`;
};

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]?.toUpperCase() ?? "").slice(0, 2).join("") || "?";

// Study-mode accent — the left edge colour on each class card. Kept subtle
// (used as an alpha wash on the border/left rail) so cards don't feel loud.
const modeAccent = (mode: string | null): string => {
  const m = (mode || "").toLowerCase();
  if (m.includes("online")) return "#0ea5e9";
  if (m.includes("hybrid")) return "#f59e0b";
  if (m.includes("person") || m === "offline") return "#8b5cf6";
  return "#94a3b8";
};

const humanMode = (mode: string | null): string => {
  const m = (mode || "").toLowerCase();
  if (m.includes("online")) return "Online";
  if (m.includes("hybrid")) return "Hybrid";
  if (m.includes("person") || m === "offline") return "In-Person";
  return mode || "—";
};

// ── Small stat chip used in the student summary strip ─────────────────
const SummaryChip: React.FC<{ label: string; value: React.ReactNode; accent?: string }> = ({ label, value, accent }) => (
  <Box
    sx={{
      display: "inline-flex", alignItems: "baseline", gap: 0.5,
      px: 1, py: 0.4, borderRadius: "999px",
      bgcolor: accent ? alpha(accent, 0.08) : "#f1f5f9",
      border: `1px solid ${accent ? alpha(accent, 0.24) : HAIRLINE}`,
    }}
  >
    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: accent || INK, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.03em" }}>
      {label}
    </Typography>
  </Box>
);

export default function TimetableGrid({ student }: { student: StudentBlock }) {
  const daysWithClasses = student.days.filter(
    (d) => Array.isArray(d.classes) && d.classes.length > 0
  );
  const weekLabel = formatWeekRange(student.week_start, student.week_end);

  return (
    <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, borderColor: HAIRLINE }}>
      {/* ═══ Student summary header ═════════════════════════════════════ */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44, height: 44, borderRadius: "50%",
              bgcolor: alpha(NAVY, 0.1), color: NAVY,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 15,
              flexShrink: 0,
            }}
          >
            {initialsOf(student.student_name)}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
              {student.student_name}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.25 }}>
              {student.grade ? `Grade ${student.grade}` : "Grade —"}
              {" • "}
              Ref: {student.reference_id || "—"}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            px: 1.25, py: 0.5, borderRadius: 1.5,
            bgcolor: alpha(NAVY, 0.06),
            border: `1px solid ${alpha(NAVY, 0.16)}`,
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>
            {weekLabel}
          </Typography>
        </Box>
      </Stack>

      {/* Stat chips under the header */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        <SummaryChip label="Classes" value={student.summary.classes} />
        <SummaryChip label="Hours" value={student.summary.hours} />
        <SummaryChip label="Staff" value={student.summary.staff} />
        <SummaryChip label="Subjects" value={student.summary.subjects.length} />
        <SummaryChip label="Online" value={student.summary.online} accent="#0ea5e9" />
        <SummaryChip label="In-Person" value={student.summary.in_person} accent="#8b5cf6" />
        {student.summary.locations.length > 0 && (
          <SummaryChip label={student.summary.locations.length === 1 ? "Location" : "Locations"}
            value={student.summary.locations.join(", ")} />
        )}
        {student.summary.conflicts > 0 && (
          <Chip
            size="small"
            color="warning"
            icon={<WarningAmberOutlinedIcon sx={{ fontSize: "14px !important" }} />}
            label={`${student.summary.conflicts} conflict${student.summary.conflicts === 1 ? "" : "s"}`}
            sx={{ height: 24, fontSize: 11, fontWeight: 700 }}
          />
        )}
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      {/* ═══ Weekly timetable ═════════════════════════════════════════════ */}
      {daysWithClasses.length === 0 ? (
        <Box
          sx={{
            py: 5, textAlign: "center",
            borderRadius: 2, border: `1px dashed ${HAIRLINE}`,
            bgcolor: "#fafbfc",
          }}
        >
          <EventBusyOutlinedIcon sx={{ fontSize: 36, color: alpha(NAVY, 0.35), mb: 1 }} />
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: INK }}>
            No classes scheduled
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.5 }}>
            This student has no classes during {weekLabel}.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {daysWithClasses.map((d) => (
            <Box key={d.date} className="stur-day-block">
              {/* Day header */}
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                  {d.day}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                  {formatDayHeader(d.date)}
                </Typography>
              </Stack>

              <Stack spacing={1.25}>
                {d.classes.map((c) => {
                  const accent = modeAccent(c.mode);
                  return (
                    <Box
                      key={c.occurrence_id}
                      className="stur-class-card"
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "auto 1fr",
                          md: "auto 130px 1fr auto",
                        },
                        alignItems: "center",
                        columnGap: 1.5, rowGap: 1,
                        p: 1.5, pl: 2,
                        borderRadius: 2,
                        border: `1px solid ${HAIRLINE}`,
                        borderLeft: `4px solid ${accent}`,
                        bgcolor: "#fff",
                        transition: "box-shadow 120ms, border-color 120ms",
                        "&:hover": {
                          borderColor: alpha(accent, 0.55),
                          boxShadow: "0 1px 6px rgba(15,23,42,0.06)",
                        },
                      }}
                    >
                      {/* Checkbox — for printed review */}
                      <CheckBoxOutlineBlankIcon
                        fontSize="small"
                        sx={{ color: "#cbd5e1", gridRow: { xs: "1", md: "auto" } }}
                      />

                      {/* Time block */}
                      <Box
                        sx={{
                          gridColumn: { xs: "1 / -1", md: "auto" },
                          minWidth: 0,
                          display: "flex", flexDirection: { xs: "row", md: "column" },
                          alignItems: { xs: "center", md: "flex-start" }, gap: { xs: 0.75, md: 0 },
                        }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
                          {to12h(c.start_time)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED, lineHeight: 1.2 }}>
                          {to12h(c.end_time)}
                        </Typography>
                      </Box>

                      {/* Class details */}
                      <Box sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "auto" } }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
                          {c.subject || "Class"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ mt: 0.35, flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <GroupsOutlinedIcon sx={{ fontSize: 13, color: MUTED }} />
                            {c.staff_id ? (
                              <MuiLink
                                component={RouterLink}
                                to={`/team-history/${c.staff_id}`}
                                underline="hover"
                                sx={{ fontSize: 12.5, color: NAVY, fontWeight: 600 }}
                              >
                                {c.staff_name || "Staff TBD"}
                              </MuiLink>
                            ) : (
                              <Typography sx={{ fontSize: 12.5, color: MUTED, fontWeight: 600 }}>
                                {c.staff_name || "Staff TBD"}
                              </Typography>
                            )}
                          </Stack>
                          <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#cbd5e1" }} />
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {(c.mode || "").toLowerCase().includes("online") ? (
                              <LaptopMacOutlinedIcon sx={{ fontSize: 13, color: accent }} />
                            ) : (
                              <PlaceOutlinedIcon sx={{ fontSize: 13, color: accent }} />
                            )}
                            <Typography sx={{ fontSize: 12.5, color: accent, fontWeight: 600 }}>
                              {humanMode(c.mode)}
                            </Typography>
                          </Stack>
                          <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#cbd5e1" }} />
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ScheduleOutlinedIcon sx={{ fontSize: 13, color: MUTED }} />
                            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                              {c.location || "Location not assigned"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>

                      {/* Muted IDs (right on desktop, below on mobile) */}
                      <Box
                        sx={{
                          gridColumn: { xs: "1 / -1", md: "auto" },
                          textAlign: { xs: "left", md: "right" },
                          minWidth: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: MUTED, lineHeight: 1.4 }}>
                          {c.class_id ? (
                            <MuiLink
                              component={RouterLink}
                              to={`/class-detail/${c.class_id}`}
                              underline="hover"
                              sx={{ color: MUTED }}
                            >
                              Class #{c.class_id}
                            </MuiLink>
                          ) : (
                            <>Class #—</>
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: MUTED, lineHeight: 1.4 }}>
                          Occurrence #{c.occurrence_id}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* ═══ Student / Parent Review ═════════════════════════════════════ */}
      <Box
        className="stur-review"
        sx={{
          mt: 3,
          borderRadius: 2,
          border: `1px solid ${REVIEW_BORDER}`,
          bgcolor: REVIEW_BG,
          p: { xs: 2, md: 2.5 },
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
          Student / Parent Review
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.5 }}>
          Use this section when reviewing the weekly timetable with the student or parent.
        </Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: MUTED }} />
            <Typography sx={{ fontSize: 13, color: INK }}>All classes are correct</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: MUTED }} />
            <Typography sx={{ fontSize: 13, color: INK }}>Changes requested</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: MUTED }} />
            <Typography sx={{ fontSize: 13, color: INK }}>Follow-up required</Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            columnGap: 2, rowGap: 1.5,
          }}
        >
          <ReviewLine label="Reviewed by" />
          <ReviewLine label="Date" />
          <ReviewLine label="Signature / Initials" />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
            Notes
          </Typography>
          <Box
            sx={{
              minHeight: 90,
              borderRadius: 1.5,
              border: `1px solid ${REVIEW_BORDER}`,
              bgcolor: "#fff",
              // Lined-paper look via repeating linear gradient, matches printout expectations.
              backgroundImage: `repeating-linear-gradient(
                transparent,
                transparent 26px,
                ${alpha(REVIEW_BORDER, 0.9)} 26px,
                ${alpha(REVIEW_BORDER, 0.9)} 27px
              )`,
            }}
          />
        </Box>
      </Box>
    </Card>
  );
}

// ── Signature-line helper for the review block ────────────────────────
const ReviewLine: React.FC<{ label: string }> = ({ label }) => (
  <Box>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Box sx={{ mt: 0.75, height: 22, borderBottom: `1px solid ${REVIEW_BORDER}` }} />
  </Box>
);
